import {getCookie, setCookie, deleteCookie} from 'cookies-next';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_OPTIONS = {
  maxAge: 365 * 24 * 60 * 60,
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production'
};

export function getCookiePreferences(): CookiePreferences | null {
  const savedPreferences = getCookie(COOKIE_CONSENT_KEY);
  if (savedPreferences) {
    try {
      return JSON.parse(savedPreferences as string);
    } catch (error) {
      console.error('Failed to parse cookie preferences:', error);
      return null;
    }
  }
  return null;
}

export function saveCookiePreferences(preferences: CookiePreferences): void {
  setCookie(
    COOKIE_CONSENT_KEY,
    JSON.stringify(preferences),
    COOKIE_CONSENT_OPTIONS
  );
}

export function clearCookiePreferences(): void {
  deleteCookie(COOKIE_CONSENT_KEY, COOKIE_CONSENT_OPTIONS);
}

export function hasConsentFor(category: keyof CookiePreferences): boolean {
  const preferences = getCookiePreferences();
  if (!preferences) return false;
  return preferences[category] || false;
}

export function hasAnyConsent(): boolean {
  const preferences = getCookiePreferences();
  return preferences !== null;
}

export function isAnalyticsEnabled(): boolean {
  const preferences = getCookiePreferences();
  return preferences?.analytics || false;
}

export function disableAnalytics(): void {
  const analyticsScript = document.querySelector('script[src*="rybbit"]');
  if (analyticsScript) {
    analyticsScript.remove();
  }

  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const [name] = cookie.split('=');
    if (
      name &&
      (name.trim().toLowerCase().includes('rybbit') ||
        name.trim().toLowerCase().includes('analytics'))
    ) {
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });

  if (typeof window !== 'undefined') {
    if ((window as any).rybbit) {
      (window as any).rybbit = undefined;
    }

    if ((window as any).gtag) {
      (window as any).gtag = () => {};
    }

    if ((window as any).fbq) {
      (window as any).fbq = () => {};
    }
  }

  console.log('Analytics tracking disabled');
}

export function initializeServicesBasedOnConsent(): void {
  const preferences = getCookiePreferences();

  if (!preferences) {
    console.log('No cookie consent found, services not initialized');
    return;
  }

  if (preferences.analytics) {
    console.log('Initializing analytics services...');
  } else {
    disableAnalytics();
  }

  if (preferences.marketing) {
    console.log('Initializing marketing services...');
  }
}

const analyticsContent = {
  en: {
    short:
      'We use Rybbit Analytics to understand how you use our platform and improve your experience. This privacy-focused analytics tool helps us make data-driven decisions while respecting your privacy.',
    detailed:
      'What is Rybbit Analytics? Rybbit is a privacy-first analytics platform that helps us understand how visitors interact with our website and applications. Unlike traditional analytics tools, Rybbit is designed with privacy at its core and complies with GDPR without requiring cookie consent for basic usage tracking.\n\nWhat data does Rybbit collect?\n• Page views and navigation patterns\n• Device type and browser information (anonymized)\n• Geographic location (country/region level only)\n• Time spent on pages and user interactions\n• Referral sources (how you found our site)\n\nWhat data does Rybbit NOT collect?\n• Personal identifiable information (PII)\n• Email addresses or contact details\n• Individual user profiles or tracking across sessions\n• Detailed location data (IP addresses are anonymized)\n• Cross-site tracking or behavioral profiling\n\nHow we use this data:\n• Improve website performance and user experience\n• Understand which features are most valuable to our users\n• Optimize our platform based on real usage patterns\n• Make informed decisions about product development\n\nData retention and hosting: Analytics data is retained for 24 months and then automatically deleted. All data is processed and stored on our own dedicated Rybbit installation running on our AWS tenant in Stockholm, Sweden. This means your data never leaves our secure infrastructure and remains within the EU at all times.\n\nYour privacy rights: You can opt-out of analytics tracking at any time through our privacy settings. This will not affect your ability to use our platform.'
  },
  no: {
    short:
      'Vi bruker Rybbit Analytics for å forstå hvordan du bruker plattformen vår og forbedre din opplevelse. Dette personvernfokuserte analyseverktøyet hjelper oss med å ta datadrevne beslutninger samtidig som vi respekterer ditt personvern.',
    detailed:
      'Hva er Rybbit Analytics? Rybbit er en personvernvennlig analyseplattform som hjelper oss å forstå hvordan besøkende interagerer med nettsiden og applikasjonene våre. I motsetning til tradisjonelle analyseverktøy er Rybbit designet med personvern i kjernen og overholder GDPR uten å kreve cookie-samtykke for grunnleggende brukssporing.\n\nHvilke data samler Rybbit inn?\n• Sidevisninger og navigasjonsmønstre\n• Enhetstype og nettleserinformasjon (anonymisert)\n• Geografisk plassering (kun land/region-nivå)\n• Tid brukt på sider og brukerinteraksjoner\n• Henvisningskilder (hvordan du fant siden vår)\n\nHvilke data samler Rybbit IKKE inn?\n• Personlig identifiserbar informasjon (PII)\n• E-postadresser eller kontaktdetaljer\n• Individuelle brukerprofiler eller sporing på tvers av økter\n• Detaljerte stedsdata (IP-adresser anonymiseres)\n• Krysside-sporing eller atferdsprofiling\n\nHvordan vi bruker disse dataene:\n• Forbedre nettstedets ytelse og brukeropplevelse\n• Forstå hvilke funksjoner som er mest verdifulle for våre brukere\n• Optimalisere plattformen vår basert på faktiske bruksmønstre\n• Ta informerte beslutninger om produktutvikling\n\nDatalagring og hosting: Analysedata lagres i 24 måneder og slettes deretter automatisk. All data prosesseres og lagres på vår egen dedikerte Rybbit-installasjon som kjører på vår AWS-tenant i Stockholm, Sverige. Dette betyr at dataene dine aldri forlater vår sikre infrastruktur og forblir innenfor EU til enhver tid.\n\nDine personvernrettigheter: Du kan melde deg ut av analysesporing når som helst gjennom våre personverninstillinger. Dette vil ikke påvirke din mulighet til å bruke plattformen vår.'
  },
  sv: {
    short:
      'Vi använder Rybbit Analytics för att förstå hur du använder vår plattform och förbättra din upplevelse. Detta integritetsfokuserade analysverktyg hjälper oss att fatta datadrivna beslut samtidigt som vi respekterar din integritet.',
    detailed:
      'Vad är Rybbit Analytics? Rybbit är en integritetsvänlig analysplattform som hjälper oss att förstå hur besökare interagerar med vår webbplats och applikationer. Till skillnad från traditionella analysverktyg är Rybbit designad med integritet i kärnan och följer GDPR utan att kräva cookie-samtycke för grundläggande användningsspårning.\n\nVilken data samlar Rybbit in?\n• Sidvisningar och navigationsmönster\n• Enhetstyp och webbläsarinformation (anonymiserad)\n• Geografisk plats (endast land/region-nivå)\n• Tid spenderad på sidor och användarinteraktioner\n• Hänvisningskällor (hur du hittade vår sida)\n\nVilken data samlar Rybbit INTE in?\n• Personligt identifierbar information (PII)\n• E-postadresser eller kontaktuppgifter\n• Individuella användarprofiler eller spårning över sessioner\n• Detaljerad platsdata (IP-adresser anonymiseras)\n• Korssidesspårning eller beteendeprofilering\n\nHur vi använder denna data:\n• Förbättra webbplatsens prestanda och användarupplevelse\n• Förstå vilka funktioner som är mest värdefulla för våra användare\n• Optimera vår plattform baserat på verkliga användningsmönster\n• Fatta välgrundade beslut om produktutveckling\n\nDatalagring och hosting: Analysedata lagras i 24 månader och raderas sedan automatiskt. All data bearbetas och lagras på vår egen dedikerade Rybbit-installation som körs på vår AWS-tenant i Stockholm, Sverige. Detta innebär att din data aldrig lämnar vår säkra infrastruktur och forblir inom EU hela tiden.\n\nDina integritetsrättigheter: Du kan välja bort analysesporing när som helst genom våra integritetsinställningar. Detta kommer inte att påverka din möjlighet att använda vår plattform.'
  },
  da: {
    short:
      'Vi bruger Rybbit Analytics til at forstå, hvordan du bruger vores platform og forbedre din oplevelse. Dette privatlivsfokuserede analyseværktøj hjælper os med at træffe datadrevne beslutninger, samtidig med at vi respekterer dit privatliv.',
    detailed:
      'Hvad er Rybbit Analytics? Rybbit er en privatlivsvenlig analyseplatform, der hjælper os med at forstå, hvordan besøgende interagerer med vores hjemmeside og applikationer. I modsætning til traditionelle analyseværktøjer er Rybbit designet med privatliv i kernen og overholder GDPR uden at kræve cookie-samtykke til grundlæggende brugeropsporing.\n\nHvilke data indsamler Rybbit?\n• Sidevisninger og navigationsmønstre\n• Enhedstype og browserinformation (anonymiseret)\n• Geografisk placering (kun land/region-niveau)\n• Tid brugt på sider og brugerinteraktioner\n• Henvisningskilder (hvordan du fandt vores side)\n\nHvilke data indsamler Rybbit IKKE?\n• Personligt identificerbar information (PII)\n• E-mailadresser eller kontaktoplysninger\n• Individuelle brugerprofiler eller sporing på tværs af sessioner\n• Detaljerede lokationsdata (IP-adresser anonymiseres)\n• Tværside-sporing eller adfærdsprofiling\n\nHvordan vi bruger disse data:\n• Forbedre hjemmesidens ydeevne og brugeroplevelse\n• Forstå hvilke funktioner, der er mest værdifulle for vores brugere\n• Optimere vores platform baseret på faktiske brugsmønstre\n• Træffe informerede beslutninger om produktudvikling\n\nDataopbevaring og hosting: Analysedata opbevares i 24 måneder og slettes derefter automatisk. Alle data behandles og gemmes på vores egen dedikerede Rybbit-installation, der kører på vores AWS-tenant i Stockholm, Sverige. Dette betyder, at dine data aldrig forlader vores sikre infrastruktur og forbliver inden for EU til enhver tid.\n\nDine privatlivsrettigheder: Du kan fravælge analysesporing til enhver tid gennem vores privatlivsindstillinger. Dette vil ikke påvirke din mulighed for at bruge vores platform.'
  }
};

export function getRybbitAnalyticsContent(
  locale: 'en' | 'no' | 'sv' | 'da' = 'en'
) {
  return analyticsContent[locale];
}
